/**
 * Procedural Hover Sound Generator
 * 
 * Creates a very subtle, soft sound for UI hover effects.
 * Designed to be barely noticeable but add tactile feedback.
 */

import { PROCEDURAL_PARAMS } from '../audioConfig.js';

/**
 * Create and play a hover sound
 * @param {AudioContext} ctx - Web Audio context
 * @param {GainNode} destination - Destination gain node
 */
export function createHoverSound(ctx, destination) {
  if (!ctx || !destination) return;

  const params = PROCEDURAL_PARAMS.hover;
  const now = ctx.currentTime;

  // Create a very soft, high-frequency blip
  const oscillator = ctx.createOscillator();
  oscillator.type = params.type;
  oscillator.frequency.setValueAtTime(params.frequency, now);
  
  // Slight pitch rise for hover-in feel
  oscillator.frequency.linearRampToValueAtTime(
    params.frequency * 1.1,
    now + params.duration
  );

  // Very gentle envelope
  const gainNode = ctx.createGain();
  const gain = params.gain ?? 0.15;
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(gain, now + params.attack);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  // Add a subtle low-pass filter for smoothness
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(2000, now);

  // Connect and play
  oscillator.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + params.duration + 0.01);

  // Cleanup
  oscillator.onended = () => {
    oscillator.disconnect();
    filter.disconnect();
    gainNode.disconnect();
  };
}

/**
 * Create hover-out sound (optional, even softer)
 */
export function createHoverOutSound(ctx, destination) {
  if (!ctx || !destination) return;

  const now = ctx.currentTime;

  const oscillator = ctx.createOscillator();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(700, now);
  oscillator.frequency.linearRampToValueAtTime(600, now + 0.03);

  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.08, now + 0.002);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

  oscillator.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + 0.04);

  oscillator.onended = () => {
    oscillator.disconnect();
    gainNode.disconnect();
  };
}
